"use client";

/**
 * ThemeToggle — sun/moon button for the dashboard header (W4.H,
 * audit item #12).
 *
 * The light + dark palettes are both already defined in globals.css
 * and `next-themes` is already wired in providers/index.tsx with
 * `attribute="class"` and `defaultTheme="dark"`. This component is
 * the missing UI affordance.
 *
 * Hydration check: next-themes renders `undefined` for theme/resolvedTheme
 * on the very first SSR pass, so we guard against rendering the
 * wrong icon (and the resulting hydration mismatch warning) with a
 * useSyncExternalStore hydration gate — the server snapshot renders
 * the placeholder and the client patches to the real icon in a
 * single pre-paint commit (no post-mount setState, no flicker).
 */

import { useSyncExternalStore } from "react";
import { useTheme } from "next-themes";
import { MoonIcon, SunIcon } from "lucide-react";

import { Button } from "@/components/ui/button";

// Static store: never changes after hydration, so subscribe is a no-op.
const emptySubscribe = () => {
  return () => {
    // Nothing to unsubscribe — the snapshot never changes post-hydration.
  };
};
const getHydratedSnapshot = () => true;
const getHydratedServerSnapshot = () => false;

export function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const hydrated = useSyncExternalStore(
    emptySubscribe,
    getHydratedSnapshot,
    getHydratedServerSnapshot,
  );

  if (!hydrated) {
    // Placeholder slot of the same dimensions to avoid layout shift
    // on hydration. aria-hidden because there's no real action yet.
    return (
      <Button
        size="icon"
        variant="ghost"
        className="size-8"
        aria-hidden="true"
        disabled
      >
        <span className="sr-only">Theme</span>
      </Button>
    );
  }

  const isDark = resolvedTheme === "dark";
  const next = isDark ? "light" : "dark";
  const label = isDark ? "Switch to light mode" : "Switch to dark mode";

  return (
    <Button
      size="icon"
      variant="ghost"
      onClick={() => setTheme(next)}
      aria-label={label}
      title={label}
      className="size-8"
    >
      {isDark ? (
        <SunIcon className="size-4" />
      ) : (
        <MoonIcon className="size-4" />
      )}
    </Button>
  );
}
